import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { ROLE_LABELS } from "@/lib/constants";
import {
  LayoutDashboard, Building2, Map, Home, Users, Handshake,
  UserSquare2, Presentation, Settings, BarChart3, LogOut, Menu, X, Eye,
  ChevronLeft, ChevronRight, Wallet, Inbox, Files,
} from "lucide-react";

const NAV_GROUPS = ["", "Proje ve Stok", "Müşteri Yönetimi", "Satış ve Finans", "Yönetim"];
const ALL_NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { group: "Proje ve Stok", to: "/projects", label: "Projeler", icon: Building2, testid: "nav-projects" },
  { group: "Proje ve Stok", to: "/stock-map", label: "Stok Haritası", icon: Map, testid: "nav-stock-map" },
  { group: "Proje ve Stok", to: "/units", label: "Stok Listesi", icon: Home, testid: "nav-units" },
  { group: "Müşteri Yönetimi", to: "/customers", label: "Müşteriler", icon: Users, testid: "nav-customers" },
  { group: "Müşteri Yönetimi", to: "/leads", label: "Web Talepleri", icon: Inbox, testid: "nav-leads" },
  { group: "Satış ve Finans", to: "/sales", label: "Rezervasyon & Satış", icon: Handshake, testid: "nav-sales" },
  { group: "Satış ve Finans", to: "/finance", label: "Ödeme ve Komisyon", icon: Wallet, testid: "nav-finance" },
  { group: "Satış ve Finans", to: "/documents", label: "Belge ve Sözleşmeler", icon: Files, testid: "nav-documents" },
  { group: "Yönetim", to: "/team", label: "Satış Ekibi", icon: UserSquare2, testid: "nav-team" },
  { group: "Proje ve Stok", to: "/presentation", label: "Sunum Ekranı", icon: Presentation, testid: "nav-presentation" },
  { group: "Yönetim", to: "/reports", label: "Raporlar", icon: BarChart3, testid: "nav-reports" },
  { group: "Yönetim", to: "/settings", label: "Ayarlar", icon: Settings, testid: "nav-settings" },
];

export default function Layout({ children }) {
  const { user, company, logout } = useAuth();
  const nav = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [newLeads, setNewLeads] = useState(0);
  useEffect(() => {
    let alive = true, pending = false;
    setNewLeads(0);
    if (!user || user.role === "super_admin") return;
    const refresh = async () => {
      if (pending) return;
      pending = true;
      try { const { data } = await api.get("/leads/count"); if (alive) setNewLeads(data.count); }
      catch { /* Keep the last known count during a temporary connection failure. */ }
      finally { pending = false; }
    };
    refresh();
    const timer = setInterval(refresh, 30000);
    window.addEventListener("focus", refresh);
    window.addEventListener("leads-updated", refresh);
    return () => { alive = false; clearInterval(timer); window.removeEventListener("focus", refresh); window.removeEventListener("leads-updated", refresh); };
  }, [user]);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("sidebar-collapsed") === "true");

  const isViewer = user?.role === "demo_viewer";
  const NAV = user?.role === "super_admin" ? [{ to: "/platform", label: "Firma Yönetimi", icon: Building2, testid: "nav-platform" }, { to: "/settings", label: "Ayarlar", icon: Settings, testid: "nav-settings" }] : ALL_NAV.filter((n) => !(isViewer && n.hideForViewer));

  const handleLogout = async () => {
    await logout();
    nav("/login");
  };

  const toggleCollapsed = () => {
    setCollapsed((value) => {
      const next = !value;
      localStorage.setItem("sidebar-collapsed", String(next));
      return next;
    });
  };

  return (
    <div className="min-h-screen flex">
      {/* Sidebar */}
      <aside
        className={`sidebar fixed inset-y-0 left-0 w-64 ${collapsed ? "lg:w-16" : "lg:w-64"} flex-col z-40 transform transition-all duration-200 lg:translate-x-0 ${
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        } lg:flex flex`}
        data-testid="sidebar"
      >
        <button
          type="button"
          onClick={toggleCollapsed}
          className="hidden lg:flex absolute -right-2 top-20 z-50 w-4 h-8 items-center justify-center rounded-r-md border border-l-0 border-slate-200 bg-white text-slate-500 shadow-sm hover:text-slate-900 hover:border-slate-300"
          title={collapsed ? "Menüyü genişlet" : "Menüyü daralt"}
          data-testid="desktop-sidebar-toggle"
        >
          {collapsed ? <ChevronRight className="w-3 h-3" /> : <ChevronLeft className="w-3 h-3" />}
        </button>
        <div className={`h-16 px-5 flex items-center gap-3 border-b border-white/5 ${collapsed ? "lg:justify-center lg:px-3 lg:gap-0" : ""}`}>
          <div className="w-11 h-11 flex items-center justify-center overflow-hidden shrink-0">
            {user?.role === "super_admin" || company?.logo_url ? <img src={user?.role === "super_admin" ? "/brand/TIENTI_Primary_White.svg" : company.logo_url} alt={user?.role === "super_admin" ? "TIENTI" : company.name} className="w-11 h-11 object-contain" /> : <Building2 className="w-8 h-8 text-white" aria-hidden="true" />}
          </div>
          <div className={`leading-tight min-w-0 ${collapsed ? "lg:hidden" : ""}`}>
            <div className="text-white font-semibold text-sm" data-testid="sidebar-company-name">
              {company?.name || company?.short_name || "Panel"}
            </div>
            <div className="text-[11px] uppercase tracking-widest text-slate-400">Satış Paneli</div>
          </div>
        </div>
        <nav aria-label="Ana menü" className="flex-1 min-h-0 overflow-y-auto px-2 py-3">
          {NAV_GROUPS.map(group => {
            const items = NAV.filter(n => (n.group || "") === group);
            if (!items.length) return null;
            return <div role="group" aria-label={group || "Genel"} key={group} className={group ? "mt-3" : ""}>
              {group && <h2 className={`px-4 mb-1 text-sm font-semibold text-slate-300 ${collapsed ? "lg:sr-only" : ""}`} style={{ letterSpacing: 0 }}>{group}</h2>}
              {items.map((n) => {
            const Icon = n.icon;
            return (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                className={({ isActive }) => `sidebar-link flex items-center gap-3 ${group ? "pl-6 pr-4 text-[13px] font-normal" : "px-4 text-sm"} rounded-md ${collapsed ? "lg:justify-center lg:px-0 lg:gap-0" : ""} py-2 ${isActive ? "active" : ""}`}
                onClick={() => setMobileOpen(false)}
                data-testid={n.testid}
                aria-label={n.label}
                title={collapsed ? n.label : undefined}
              >
                <Icon className="w-4 h-4 shrink-0" />
                <span className={collapsed ? "lg:hidden" : ""}>{n.label}</span>
                {n.to === "/leads" && newLeads > 0 && <span aria-label={`${newLeads} yeni web talebi`} className="shrink-0 rounded-full bg-amber-500 px-1.5 text-xs font-semibold text-slate-950">{newLeads}</span>}
              </NavLink>
            );
              })}
            </div>;
          })}
        </nav>
        {isViewer && (
          <div className={`mx-3 mb-3 px-3 py-2.5 rounded-lg bg-blue-950/60 border border-blue-700/40 flex items-start gap-2 ${collapsed ? "lg:hidden" : ""}`}>
            <Eye className="w-3.5 h-3.5 text-blue-400 shrink-0 mt-0.5" />
            <div>
              <div className="text-[11px] font-semibold text-blue-300 leading-tight">İnceleme Modu</div>
              <div className="text-[10px] text-blue-400/80 mt-0.5 leading-tight">Bu hesap yalnızca görüntüleme amaçlıdır.</div>
            </div>
          </div>
        )}
        <div className={`px-4 py-4 border-t border-white/5 ${collapsed ? "lg:px-2 lg:text-center" : ""}`}>
          <div className={`tienti-poweredby ${collapsed ? "lg:hidden" : ""}`} data-testid="sidebar-poweredby">Powered by TIENTI Core</div>
          <div className="text-[10px] text-slate-500 mt-1">© {new Date().getFullYear()}</div>
        </div>
      </aside>

      {/* Backdrop for mobile */}
      {mobileOpen && (
        <div className="fixed inset-0 bg-black/50 z-30 lg:hidden" onClick={() => setMobileOpen(false)} />
      )}

      {/* Main */}
      <div className={`flex-1 min-w-0 ${collapsed ? "lg:pl-16" : "lg:pl-64"} flex flex-col min-h-screen transition-all duration-200`}>
        {/* Topbar */}
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4 lg:px-8 sticky top-0 z-20">
          <div className="flex items-center gap-3">
            <button
              className="lg:hidden p-2 -ml-2 text-slate-600"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label={mobileOpen ? "Menüyü kapat" : "Menüyü aç"}
              aria-expanded={mobileOpen}
              data-testid="mobile-menu-toggle"
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            <div className="hidden sm:block">
              <div className="text-sm font-semibold text-slate-900" data-testid="topbar-company">
                {company?.name}
              </div>
              <div className="text-xs text-slate-500">{company?.address || company?.email}</div>
            </div>
          </div>

          <div className="flex items-center gap-4">
            {isViewer && (
              <div className="hidden sm:flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-blue-50 border border-blue-200">
                <Eye className="w-3 h-3 text-blue-500" />
                <span className="text-[11px] font-medium text-blue-600">İnceleme Modu</span>
              </div>
            )}
            <div className="text-right hidden sm:block">
              <div className="text-sm font-medium text-slate-900" data-testid="topbar-user-name">{user?.name}</div>
              <div className="text-xs text-slate-500" data-testid="topbar-user-role">{ROLE_LABELS[user?.role] || user?.role}</div>
            </div>
            <div className="w-9 h-9 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center font-semibold">
              {user?.name?.[0] || "U"}
            </div>
            <button
              onClick={handleLogout}
              className="text-slate-500 hover:text-slate-900 transition-colors"
              data-testid="logout-button"
              title="Çıkış"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </header>

        <main className="flex-1 min-w-0 p-4 sm:p-6 lg:p-8 animate-fade-in">
          {children}
        </main>
      </div>
    </div>
  );
}
